import { useEffect, useMemo, useRef, useState } from 'react';
import { loadNaverMap } from './lib/naverMapLoader';
import { NAVER_MAP_CLIENT_ID } from './config/keys';

export interface NaverMapMarkerItem {
  id: string | number;
  name: string;
  lat: number;
  lng: number;
  html?: string;
  order?: number;
  shortName?: string | number;
}

interface NaverMapProps {
  clientId?: string;
  className?: string;
  style?: React.CSSProperties;
  center?: { lat: number; lng: number };
  zoom?: number;
  minHeight?: number;
  markers?: NaverMapMarkerItem[];
  mapMarkerPositionBottom?: number;
  selectedMarkerId?: string | number | null;
  autoFit?: boolean;
  showInfoWindowOnClick?: boolean;
  connectLine?: boolean;
  moveToSelectedMarker?: boolean;
  onMarkerClick?: (marker: NaverMapMarkerItem, index: number) => void;
  onZoomChanged?: (zoom: number) => void;
  onCenterChanged?: (center: { lat: number; lng: number }) => void;
  onMapClick?: () => void;
}

const DEFAULT_CENTER = { lat: 37.5665, lng: 126.9780 };
const EPSILON = 0.000001;

function isSameLatLng(
  a: { lat: number; lng: number } | null | undefined,
  b: { lat: number; lng: number } | null | undefined
) {
  if (!a || !b) return false;
  return (
    Math.abs(a.lat - b.lat) < EPSILON &&
    Math.abs(a.lng - b.lng) < EPSILON
  );
}

function buildMarkerSignature(markers: NaverMapMarkerItem[]) {
  return markers
    .map((m) => `${m.id}:${m.lat}:${m.lng}:${m.order ?? ''}:${m.shortName ?? ''}`)
    .join('|');
}

export default function NaverMap({
  clientId = NAVER_MAP_CLIENT_ID,
  className,
  style,
  center,
  zoom = 14,
  minHeight,
  markers = [],
  mapMarkerPositionBottom,
  selectedMarkerId = null,
  autoFit = true,
  showInfoWindowOnClick = true,
  connectLine = true,
  moveToSelectedMarker = false,
  onMarkerClick,
  onZoomChanged,
  onCenterChanged,
  onMapClick,
}: NaverMapProps) {
  const mapElementRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const naverRef = useRef<any>(null);

  const markerRefs = useRef<any[]>([]);
  const polylineRef = useRef<any>(null);
  const infoWindowRef = useRef<any>(null);
  const eventListenersRef = useRef<any[]>([]);

  const markerClickHandlerRef = useRef<typeof onMarkerClick>();
  const mapClickHandlerRef = useRef<typeof onMapClick>();

  const prevMarkerSignatureRef = useRef<string>('');
  const prevAppliedCenterRef = useRef<{ lat: number; lng: number } | null>(null);
  const prevAppliedZoomRef = useRef<number | null>(null);

  const [ready, setReady] = useState(false);

  markerClickHandlerRef.current = onMarkerClick;
  mapClickHandlerRef.current = onMapClick;

  const markerSignature = useMemo(() => buildMarkerSignature(markers), [markers]);

  const initialCenter = useMemo(() => {
    if (center) return center;
    if (markers.length > 0) {
      return { lat: markers[0].lat, lng: markers[0].lng };
    }
    return DEFAULT_CENTER;
  }, [center, markers]);

  useEffect(() => {
    let mounted = true;

    loadNaverMap({ clientId })
      .then((naver) => {
        if (!mounted || !mapElementRef.current) return;

        naverRef.current = naver;

        if (!mapRef.current) {
          const map = new naver.maps.Map(mapElementRef.current, {
            center: new naver.maps.LatLng(initialCenter.lat, initialCenter.lng),
            zoom,
          });

          mapRef.current = map;
          prevAppliedCenterRef.current = initialCenter;
          prevAppliedZoomRef.current = zoom;

          infoWindowRef.current = new naver.maps.InfoWindow({
            content: '',
            borderWidth: 0,
            backgroundColor: '#fff',
            anchorSize: new naver.maps.Size(12, 14),
            pixelOffset: new naver.maps.Point(0, -8),
          });

          const zoomListener = naver.maps.Event.addListener(map, 'zoom_changed', () => {
            const currentZoom = map.getZoom();
            prevAppliedZoomRef.current = currentZoom;
            onZoomChanged?.(currentZoom);
          });

          const centerListener = naver.maps.Event.addListener(map, 'center_changed', () => {
            const currentCenter = map.getCenter();
            const nextCenter = {
              lat: currentCenter.y,
              lng: currentCenter.x,
            };
            prevAppliedCenterRef.current = nextCenter;
            onCenterChanged?.(nextCenter);
          });

          const mapClickListener = naver.maps.Event.addListener(map, 'click', () => {
            mapClickHandlerRef.current?.();
          });

          eventListenersRef.current = [zoomListener, centerListener, mapClickListener];
        }

        setReady(true);
      })
      .catch((err) => {
        console.error(err);
      });

    return () => {
      mounted = false;
      const naver = naverRef.current;
      if (naver && eventListenersRef.current.length > 0) {
        eventListenersRef.current.forEach((listener) => {
          naver.maps.Event.removeListener(listener);
        });
      }
      eventListenersRef.current = [];
    };
  }, [clientId, initialCenter, zoom, onZoomChanged, onCenterChanged]);

  // 외부에서 center prop을 명시적으로 바꾼 경우에만 center 반영
  useEffect(() => {
    if (!ready || !mapRef.current || !naverRef.current || !center) return;

    const currentAppliedCenter = prevAppliedCenterRef.current;
    if (isSameLatLng(currentAppliedCenter, center)) return;

    const naver = naverRef.current;
    mapRef.current.setCenter(new naver.maps.LatLng(center.lat, center.lng));
    prevAppliedCenterRef.current = center;
  }, [ready, center]);

  // 외부에서 zoom prop을 명시적으로 바꾼 경우에만 zoom 반영
  useEffect(() => {
    if (!ready || !mapRef.current) return;

    const currentZoom = mapRef.current.getZoom();
    if (currentZoom === zoom) return;

    mapRef.current.setZoom(zoom);
    prevAppliedZoomRef.current = zoom;
  }, [ready, zoom]);

  // 마커/라인 렌더링 + marker 집합이 바뀐 경우에만 viewport 조정
  useEffect(() => {
    if (!ready || !mapRef.current || !naverRef.current) return;

    const naver = naverRef.current;
    const map = mapRef.current;

    markerRefs.current.forEach((marker) => marker?.setMap(null));
    markerRefs.current = [];

    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }

    const bounds = new naver.maps.LatLngBounds();
    const path: any[] = [];

    markers.forEach((item, index) => {
      const position = new naver.maps.LatLng(item.lat, item.lng);
      bounds.extend(position);
      path.push(position);

      const orderLabel = item.shortName ?? item.order ?? index + 1;

      const marker = new naver.maps.Marker({
        map,
        position,
        title: item.name,
        icon: {
          content: `
            <div
              style="
                width: 34px;
                height: 34px;
                border-radius: 9999px;
                background: #2563EB;
                border: 2px solid #ffffff;
                box-shadow: 0 2px 8px rgba(0,0,0,0.22);
                color: white;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 14px;
                font-weight: 700;
                line-height: 1;
              "
            >
              ${orderLabel}
            </div>
          `,
          size: new naver.maps.Size(34, 34),
          anchor: new naver.maps.Point(17, 17),
        },
      });

      naver.maps.Event.addListener(marker, 'click', () => {
        if (showInfoWindowOnClick && infoWindowRef.current) {
          const content =
            item.html ??
            `
              <div style="padding:10px 12px; font-size:13px; line-height:1.4; white-space:nowrap;">
                ${item.name}
              </div>
            `;

          infoWindowRef.current.setContent(content);
          infoWindowRef.current.open(map, marker);
        }

        markerClickHandlerRef.current?.(item, index);
      });

      markerRefs.current.push(marker);
    });

    if (connectLine && path.length >= 2) {
      polylineRef.current = new naver.maps.Polyline({
        map,
        path,
        strokeColor: '#2563EB',
        strokeOpacity: 0.85,
        strokeWeight: 4,
        strokeLineCap: 'round',
        strokeLineJoin: 'round',
      });
    }

    const markerSetChanged = prevMarkerSignatureRef.current !== markerSignature;

    if (markerSetChanged) {
      if (autoFit && markers.length >= 2) {
        map.fitBounds(bounds, {
          top: 24,
          left: 24,
          bottom: mapMarkerPositionBottom || 24,
          right: 24
        });
      } else if (markers.length === 1) {
        map.setCenter(new naver.maps.LatLng(markers[0].lat, markers[0].lng));
      }

      prevMarkerSignatureRef.current = markerSignature;
    }

    return () => {
      markerRefs.current.forEach((marker) => marker?.setMap(null));
      markerRefs.current = [];
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }
    };
  }, [
    ready,
    markers,
    markerSignature,
    autoFit,
    showInfoWindowOnClick,
    connectLine,
  ]);

  // 선택 마커는 기본적으로 지도 이동 없이 infoWindow만 열기
  useEffect(() => {
    if (!ready || !selectedMarkerId || !infoWindowRef.current || !mapRef.current) return;

    const targetIndex = markers.findIndex((m) => m.id === selectedMarkerId);
    if (targetIndex < 0) return;

    const targetMarker = markerRefs.current[targetIndex];
    const targetItem = markers[targetIndex];
    const naver = naverRef.current;
    const map = mapRef.current;

    if (!targetMarker || !targetItem || !naver) return;

    if (moveToSelectedMarker) {
      map.panTo(new naver.maps.LatLng(targetItem.lat, targetItem.lng));
    }

    infoWindowRef.current.setContent(
      targetItem.html ??
        `
          <div style="padding:10px 12px; font-size:13px; line-height:1.4; white-space:nowrap;">
            ${targetItem.name}
          </div>
        `
    );
    infoWindowRef.current.open(map, targetMarker);
  }, [ready, selectedMarkerId, markers, moveToSelectedMarker]);

  return (
    <div
      ref={mapElementRef}
      className={className}
      style={{
        width: '100%',
        height: '100%',
        minHeight: minHeight || 320,
        borderRadius: 16,
        overflow: 'hidden',
        ...style,
      }}
    />
  );
}